import React, { useState, useEffect } from 'react';
import Notification from '../Notification';

const ButtonLinksManagement = () => {
  const [buttonLinks, setButtonLinks] = useState({
    getStarted: 'https://ma.riverprime.my/registration',
    tryDemo: 'https://ma.riverprime.my/registration',
    login: 'https://ma.riverprime.my/login',
    learnMore: '/about',
    contactUs: '#contact',
    downloadMT5: 'https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/mt5setup.exe',
    openAccount: 'https://ma.riverprime.my/registration',
    viewPricing: '/pricing',
    becomePartner: 'https://ma.riverprime.my/registration',
    readMore: '/learn'
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    // Load saved settings from localStorage
    const saved = localStorage.getItem('adminButtonLinks');
    if (saved) {
      try {
        setButtonLinks(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading button links:', error);
      }
    }
  }, []);

  const handleLinkChange = (buttonName, value) => {
    setButtonLinks(prev => ({
      ...prev,
      [buttonName]: value
    }));
    setHasChanges(true);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const handleSave = () => {
    try {
      localStorage.setItem('adminButtonLinks', JSON.stringify(buttonLinks));
      setHasChanges(false);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('buttonLinksUpdated', {
        detail: buttonLinks
      }));
      
      showNotification('Button links saved successfully! ✅', 'success');
    } catch (error) {
      console.error('Error saving button links:', error);
      showNotification('Error saving button links. Please try again.', 'error');
    }
  };

  const handleReset = () => {
    const defaultLinks = {
      getStarted: 'https://ma.riverprime.my/registration',
      tryDemo: 'https://ma.riverprime.my/registration',
      login: 'https://ma.riverprime.my/login',
      learnMore: '/about',
      contactUs: '#contact',
      downloadMT5: 'https://download.mql5.com/cdn/web/metaquotes.software.corp/mt5/mt5setup.exe',
      openAccount: 'https://ma.riverprime.my/registration',
      viewPricing: '/pricing',
      becomePartner: 'https://ma.riverprime.my/registration',
      readMore: '/learn'
    };
    setButtonLinks(defaultLinks);
    setHasChanges(true);
    showNotification('Button links reset to defaults', 'info');
  };

  // Organized button categories for better management
  const buttonCategories = [
    {
      name: 'Primary Actions',
      description: 'Main call-to-action buttons',
      color: '#2652a0',
      icon: 'fas fa-star',
      buttons: [
        {
          key: 'getStarted',
          label: 'Get Started Button',
          description: 'Primary CTA button in header and hero sections',
          icon: 'fas fa-user-plus',
          example: 'Registration page, signup forms'
        },
        {
          key: 'openAccount',
          label: 'Open Account Button', 
          description: 'Account opening CTAs',
          icon: 'fas fa-plus-circle',
          example: 'Account creation, new user signup'
        },
        {
          key: 'tryDemo',
          label: 'Try Demo Button',
          description: 'Demo account creation button',
          icon: 'fas fa-play',
          example: 'Demo trading platform access'
        }
      ]
    },
    {
      name: 'User Access',
      description: 'Login and authentication buttons',
      color: '#38a169',
      icon: 'fas fa-user',
      buttons: [
        {
          key: 'login',
          label: 'Login Button',
          description: 'User login button in header',
          icon: 'fas fa-sign-in-alt',
          example: 'User portal, member dashboard'
        }
      ]
    },
    {
      name: 'Information & Support',
      description: 'Learning and contact buttons',
      color: '#3182ce',
      icon: 'fas fa-info-circle',
      buttons: [
        {
          key: 'learnMore',
          label: 'Learn More Button',
          description: 'General information buttons',
          icon: 'fas fa-info-circle',
          example: 'About pages, educational content'
        },
        {
          key: 'contactUs',
          label: 'Contact Us Button',
          description: 'Contact form and support buttons',
          icon: 'fas fa-envelope',
          example: 'Contact page, support forms'
        },
        {
          key: 'readMore',
          label: 'Read More Button',
          description: 'Content expansion buttons',
          icon: 'fas fa-book-open',
          example: 'Article pages, detailed information'
        }
      ]
    },
    {
      name: 'Platform & Tools',
      description: 'Downloads and platform access',
      color: '#ed8936',
      icon: 'fas fa-tools',
      buttons: [
        {
          key: 'downloadMT5',
          label: 'Download MT5 Button',
          description: 'MetaTrader 5 download buttons',
          icon: 'fas fa-download',
          example: 'Platform download page'
        },
        {
          key: 'viewPricing',
          label: 'View Pricing Button',
          description: 'Pricing and fee information',
          icon: 'fas fa-dollar-sign',
          example: 'Pricing page, fee schedules'
        }
      ]
    },
    {
      name: 'Business & Partnerships',
      description: 'Partnership and business development',
      color: '#805ad5',
      icon: 'fas fa-handshake',
      buttons: [
        {
          key: 'becomePartner',
          label: 'Become Partner Button',
          description: 'Partnership program buttons',
          icon: 'fas fa-handshake',
          example: 'Partner program, affiliate signup'
        }
      ]
    }
  ];

  return (
    <div style={{
      padding: '30px',
      background: '#f8fafc',
      minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      {/* Header Section */}
      <div style={{
        background: 'linear-gradient(135deg, #2652a0 0%, #1a365d 100%)',
        borderRadius: '16px',
        padding: '30px',
        marginBottom: '30px',
        color: 'white',
        boxShadow: '0 10px 30px rgba(38, 82, 160, 0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
          <i className="fas fa-link" style={{ 
            fontSize: '32px', 
            marginRight: '15px',
            padding: '12px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '12px'
          }}></i>
          <div>
            <h1 style={{
              margin: '0 0 5px 0',
              fontSize: '2.2rem',
              fontWeight: '700',
              color: 'white'
            }}>
              Button Links Management
            </h1>
            <p style={{
              margin: 0,
              fontSize: '1.1rem',
              opacity: 0.9,
              color: 'rgba(255,255,255,0.9)'
            }}>
              Manage all button URLs throughout your website with organized categories
            </p>
          </div>
        </div>
        
        {hasChanges && (
          <div style={{
            background: 'rgba(255, 221, 0, 0.2)',
            border: '1px solid rgba(255, 221, 0, 0.3)',
            borderRadius: '8px',
            padding: '12px 15px',
            marginTop: '15px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px', color: '#ffdd00' }}></i>
            <span style={{ color: '#ffdd00', fontWeight: '600' }}>
              You have unsaved changes. Don't forget to save!
            </span>
          </div>
        )}
      </div>

      {/* Button Categories */}
      <div style={{ display: 'grid', gap: '25px' }}>
        {buttonCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} style={{
            background: 'white',
            borderRadius: '16px',
            padding: '25px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0'
          }}>
            {/* Category Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '15px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              <div style={{
                background: category.color,
                color: 'white',
                padding: '12px',
                borderRadius: '12px',
                marginRight: '15px',
                fontSize: '18px'
              }}>
                <i className={category.icon}></i>
              </div>
              <div>
                <h2 style={{
                  margin: '0 0 5px 0',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1a202c'
                }}>
                  {category.name}
                </h2>
                <p style={{
                  margin: 0,
                  color: '#64748b',
                  fontSize: '14px'
                }}>
                  {category.description}
                </p>
              </div>
            </div>

            {/* Buttons in Category */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {category.buttons.map((button) => (
                <div key={button.key} style={{
                  background: '#f8fafc',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '2px solid #e2e8f0',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Button Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <div style={{
                      background: category.color,
                      color: 'white',
                      padding: '8px',
                      borderRadius: '8px',
                      marginRight: '12px',
                      fontSize: '14px'
                    }}>
                      <i className={button.icon}></i>
                    </div>
                    <div>
                      <h3 style={{
                        margin: '0 0 3px 0',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#1a202c'
                      }}>
                        {button.label}
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: '12px',
                        color: '#64748b'
                      }}>
                        {button.description}
                      </p>
                    </div>
                  </div>

                  {/* URL Input */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#2d3748',
                      fontSize: '14px'
                    }}>
                      <i className="fas fa-globe" style={{ marginRight: '6px', color: category.color }}></i>
                      Button URL
                    </label>
                    <input
                      type="url"
                      value={buttonLinks[button.key] || ''}
                      onChange={(e) => handleLinkChange(button.key, e.target.value)}
                      placeholder="Enter button destination URL"
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#1a202c',
                        background: 'white',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = category.color;
                        e.target.style.boxShadow = `0 0 0 3px ${category.color}20`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Current URL Display */}
                  <div style={{
                    background: 'white',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '12px',
                      color: '#64748b'
                    }}>
                      <i className="fas fa-link" style={{ marginRight: '6px' }}></i>
                      <span style={{ fontWeight: '500' }}>Current:</span>
                      <span style={{ 
                        marginLeft: '6px',
                        color: buttonLinks[button.key] && buttonLinks[button.key] !== '#' ? '#059669' : '#dc2626',
                        fontFamily: 'Monaco, monospace',
                        wordBreak: 'break-all'
                      }}>
                        {buttonLinks[button.key] && buttonLinks[button.key] !== '#' 
                          ? buttonLinks[button.key] 
                          : 'No URL set (using placeholder #)'
                        }
                      </span>
                    </div>
                  </div>

                  {/* Usage Example */}
                  <div style={{
                    background: 'rgba(59, 130, 246, 0.05)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    fontSize: '11px',
                    color: '#1e40af'
                  }}>
                    <i className="fas fa-lightbulb" style={{ marginRight: '5px' }}></i>
                    <strong>Usage:</strong> {button.example}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '25px',
        marginTop: '30px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h3 style={{
            margin: '0 0 5px 0',
            color: '#1a202c',
            fontSize: '1.2rem',
            fontWeight: '600'
          }}>
            Actions
          </h3>
          <p style={{
            margin: 0,
            color: '#64748b',
            fontSize: '14px'
          }}>
            Save your changes or reset to default values
          </p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button 
            onClick={handleReset}
            style={{
              padding: '12px 20px',
              border: '2px solid #e2e8f0',
              borderRadius: '10px',
              background: 'white',
              color: '#64748b',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#f59e0b';
              e.target.style.color = '#f59e0b';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#64748b';
            }}
          >
            <i className="fas fa-undo"></i>
            Reset to Defaults
          </button>
          <button 
            onClick={handleSave}
            disabled={!hasChanges}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: '10px',
              background: hasChanges 
                ? 'linear-gradient(135deg, #2652a0 0%, #1a365d 100%)' 
                : '#e2e8f0',
              color: hasChanges ? 'white' : '#94a3b8',
              fontSize: '14px',
              fontWeight: '700',
              cursor: hasChanges ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              boxShadow: hasChanges ? '0 4px 15px rgba(38, 82, 160, 0.3)' : 'none'
            }}
          >
            <i className="fas fa-save"></i>
            {hasChanges ? 'Save Changes' : 'No Changes to Save'}
          </button>
        </div>
      </div>

      {/* URL Guidelines */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '25px',
        marginTop: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{
          margin: '0 0 20px 0',
          color: '#1a202c',
          fontSize: '1.3rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center'
        }}>
          <i className="fas fa-info-circle" style={{ marginRight: '10px', color: '#3b82f6' }}></i>
          URL Guidelines & Examples
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '15px'
        }}>
          {[
            {
              icon: 'fas fa-external-link-alt',
              title: 'External Links',
              desc: 'Use full URLs for external websites',
              example: 'https://example.com/register',
              color: '#10b981'
            },
            {
              icon: 'fas fa-hashtag',
              title: 'Page Anchors',
              desc: 'Link to sections on the same page',
              example: '#contact, #about, #services',
              color: '#f59e0b'
            },
            {
              icon: 'fas fa-route',
              title: 'Internal Pages',
              desc: 'Navigate to other pages on your site',
              example: '/about, /pricing, /contact',
              color: '#8b5cf6'
            },
            {
              icon: 'fas fa-envelope',
              title: 'Email Links',
              desc: 'Open email client with pre-filled address',
              example: 'mailto:support@riverprime.com',
              color: '#ef4444'
            }
          ].map((guide, index) => (
            <div key={index} style={{
              background: '#f8fafc',
              padding: '15px',
              borderRadius: '10px',
              border: `2px solid ${guide.color}20`,
              borderLeft: `4px solid ${guide.color}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <i className={guide.icon} style={{ 
                  color: guide.color, 
                  marginRight: '8px',
                  fontSize: '16px'
                }}></i>
                <strong style={{ color: '#1a202c', fontSize: '14px' }}>{guide.title}</strong>
              </div>
              <p style={{ 
                margin: '0 0 8px 0',
                fontSize: '12px',
                color: '#64748b'
              }}>
                {guide.desc}
              </p>
              <code style={{
                background: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                color: guide.color,
                border: `1px solid ${guide.color}30`
              }}>
                {guide.example}
              </code>
            </div>
          ))}
        </div>
      </div>

      {/* Notification Component */}
      <Notification 
        show={notification.show}
        message={notification.message}
        type={notification.type}
      />
    </div>
  );
};

export default ButtonLinksManagement;